'use client'

import type { Route } from 'next'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import { BrandLogo } from '@/components/brand/brand-logo'
import type { Brand, FooterContent, Service, Social, NavItem } from '@/config'

const NewsletterForm = dynamic(
  () => import('@/components/forms/newsletter-form').then(mod => mod.NewsletterForm),
  {
    ssr: false,
    loading: () => (
      <div className="rounded-2xl border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
        Newsletter signup loads on demand.
      </div>
    ),
  },
)

type SiteFooterProps = Readonly<{
  brand: Brand
  footer: FooterContent
  socials: Social[]
  services: Service[]
  utilityLinks: NavItem[]
}>

export function SiteFooter({ brand, footer, socials, services, utilityLinks }: SiteFooterProps) {
  return (
    <footer className="border-t border-border/70 bg-background">
      <div className="mx-auto flex w-full max-w-[var(--container-page)] flex-col gap-6 px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid gap-8 md:grid-cols-[1.5fr_1fr] md:items-start">
          <div className="space-y-3">
            <Link
              href="/"
              aria-label={`${brand.name} home`}
              className="inline-flex rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <BrandLogo
                brand={brand}
                sizes="168px"
                className="h-10 w-[168px]"
              />
            </Link>
            <p className="max-w-xl text-sm leading-6 text-muted-foreground">{footer.description}</p>
            <div className="max-w-md pt-2">
              <NewsletterForm compact />
            </div>
          </div>

          <div className="grid gap-4 text-sm">
            <div className="grid gap-2">
              <p className="font-medium text-foreground">{footer.utilityLabel}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {utilityLinks.map(item => (
                  <Link
                    key={item.href}
                    href={item.href as Route}
                    target={item.external ? '_blank' : undefined}
                    rel={item.external ? 'noreferrer' : undefined}
                    className="text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            {services.length ? (
              <div className="grid gap-2">
                <p className="font-medium text-foreground">{footer.servicesLabel}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {services.map(service => (
                    <Link
                      key={service.href}
                      href={service.href as Route}
                      className="text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {service.label}
                    </Link>
                  ))}
                </div>
              </div>
            ) : null}

            {socials.length ? (
              <div className="grid gap-2">
                <p className="font-medium text-foreground">{footer.socialsLabel}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {socials.map(social => (
                    <a
                      key={social.href}
                      href={social.href}
                      target="_blank"
                      rel="noreferrer"
                      className="text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {social.label}
                    </a>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col gap-2 border-t border-border/70 pt-4 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>{footer.copyright}</p>
          <p>{brand.legalName}</p>
        </div>
      </div>
    </footer>
  )
}
